import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { resolveApiBaseUrl } from '../services/resolveApiBaseUrl';
import { FRONTEND_BUILD_COMMIT, fetchBackendVersion } from '../services/buildInfo';

// #224 — first 8 chars of a full SHA reads as "a commit"; "unknown" (the
// honest fallback when neither RENDER_GIT_COMMIT nor git resolved one) passes
// through unchanged rather than being truncated into something odd.
export function shortCommit(commit) {
  if (!commit || commit === 'unknown') return 'unknown';
  return commit.slice(0, 8);
}

// Settings > Profile "Build" row (#224): shows the frontend bundle's own
// baked-in commit alongside the backend's live `/health` commit, so a
// frontend/backend commit mismatch after a partial deploy is visible without
// Render-API archaeology (today that requires manually checking Render).
//
// `frontendCommit`/`fetchVersion` are injectable (defaulting to the real
// baked-in constant and the real fetch) purely so tests can exercise the
// mismatch branch without module-mocking a build-time constant.
export default function BuildInfo({ frontendCommit = FRONTEND_BUILD_COMMIT, fetchVersion = fetchBackendVersion } = {}) {
  const [backend, setBackend] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchVersion(resolveApiBaseUrl()).then((v) => { if (!cancelled) setBackend(v); });
    return () => { cancelled = true; };
  }, [fetchVersion]);

  const mismatch = !!(backend
    && frontendCommit !== 'unknown'
    && backend.commit
    && backend.commit !== 'unknown'
    && backend.commit !== frontendCommit);

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader title="Build" slotProps={{ title: { variant: 'h6' } }} />
      <CardContent>
        <Stack spacing={0.5}>
          <Typography variant="body2">
            Frontend: <code>{shortCommit(frontendCommit)}</code>
          </Typography>
          <Typography variant="body2">
            Backend: <code>{backend ? shortCommit(backend.commit) : 'unavailable'}</code>
          </Typography>
          {mismatch && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Frontend and backend are running different commits — a deploy may be partially rolled out.
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
